/**
 * Auth0 SPA client singleton (Phase 2 #5B).
 *
 * Configured from Vite env vars — set in apps/web/.env.local for local dev:
 *
 *   VITE_AUTH0_DOMAIN=dev-newbrainllc.us.auth0.com
 *   VITE_AUTH0_CLIENT_ID=<SPA app client_id from the Auth0 dashboard>
 *   VITE_AUTH0_AUDIENCE=https://api.dev.re-platform.app
 *
 * The audience must match the backend's `AUTH0_AUDIENCE` so the access token
 * Cognito federation consumes is also the same token FastAPI's
 * CompositeJWTValidator accepts on the RS256 path.
 *
 * Cache lives in memory — we do not write Auth0 state to localStorage. The
 * backend HS256 session JWT (returned by passkey login) is what we persist
 * for bearer-token API calls; the Auth0 access token is short-lived and only
 * round-trips Cognito for AWS creds.
 */

import { Auth0Client, type Auth0ClientOptions } from "@auth0/auth0-spa-js";

const domain = import.meta.env.VITE_AUTH0_DOMAIN as string | undefined;
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID as string | undefined;
const audience = import.meta.env.VITE_AUTH0_AUDIENCE as string | undefined;
const organization = import.meta.env.VITE_AUTH0_ORG_ID as string | undefined;

let _client: Auth0Client | null = null;

export function isAuth0Configured(): boolean {
  return Boolean(domain && clientId && audience);
}

export function getAuth0Client(): Auth0Client {
  if (!isAuth0Configured()) {
    throw new Error(
      "Auth0 SPA client not configured — set VITE_AUTH0_DOMAIN / VITE_AUTH0_CLIENT_ID / VITE_AUTH0_AUDIENCE",
    );
  }
  if (_client) return _client;
  const options: Auth0ClientOptions = {
    domain: domain!,
    clientId: clientId!,
    authorizationParams: {
      audience: audience!,
      redirect_uri: `${window.location.origin}/auth/callback`,
      // openid required to get an id_token (which Cognito federation needs to
      // validate the user); profile + email round out the standard claims.
      scope: "openid profile email",
      // Auth0 only adds `org_id` to the JWT when the user logs in within an
      // Organization context. Without it, the backend's extract_tenant_context
      // rejects the token. For Phase 2 #5B we hardcode one org via env; later
      // phases can swap this for an /identity/org-lookup-by-email pre-flight.
      ...(organization ? { organization } : {}),
    },
    cacheLocation: "memory",
  };
  _client = new Auth0Client(options);
  return _client;
}

/**
 * Logins-map key for Cognito's GetCredentialsForIdentity. This MUST be the
 * bare hostname — no scheme, no trailing slash. Cognito strips `https://`
 * AND the trailing slash from the token's `iss` claim before comparing to
 * the registered IAM OIDC provider URL, but does NOT strip the trailing
 * slash from the providerName side. So the IAM OIDC provider URL ALSO has
 * to be registered without a trailing slash; otherwise iss-vs-providerName
 * fails with "Invalid login token. Issuer doesn't match providerName".
 *
 * See [[cognito-auth0-federation-stuck]] memory for the full debug story.
 */
export function auth0LoginsKey(): string {
  if (!domain) {
    throw new Error("VITE_AUTH0_DOMAIN not configured");
  }
  return domain;
}
